import { useEffect, useState } from "react";
import { Phone } from "lucide-react";
import registerRetellCall from "../utils/register-retell-call";
import retellWebClient from "../utils/retell-web-client";

const AdminPanel = () => {
  const [agentId, setAgentId] = useState("");
  const [callDetails, setCallDetails] = useState(null);
  const [isCallActive, setIsCallActive] = useState(false);
  const [transcript, setTranscript] = useState("");

  useEffect(() => {
    retellWebClient.on("update", (update) => {
      const lastTranscript = update?.transcript?.slice(-1)[0];
      setTranscript(lastTranscript?.role + ": " + lastTranscript?.content);
    });
    retellWebClient.on("call_ended", () => {
      setIsCallActive(false);
    });

    return () => {
      retellWebClient.stopConversation();
    };
  }, []);

  const handleStartCall = async () => {
    try {
      const registerCallResponse = await registerRetellCall(agentId);
      setCallDetails(registerCallResponse);

      if (registerCallResponse.call_id) {
        await retellWebClient.startConversation({
          callId: registerCallResponse.call_id,
          sampleRate: registerCallResponse.sample_rate,
          enableUpdate: true
        });
        setIsCallActive(true);
      }
    } catch (error) {
      console.error("Error starting call:", error);
    }
  };

  const handleEndCall = async () => {
    try {
      retellWebClient.stopConversation();
      setTranscript("Call ended. Transcript will appear here.");
    } catch (error) {
      console.error("Error ending call:", error);
    }
  };

  const theme = {
    bg: "#1e1e1e",
    text: "#e0e0e0",
    primary: "rgba(157, 78, 221, 0.7)",
    danger: "#ff6b6b",
    inputBg: "rgba(45, 45, 45, 0.5)",
    cardBg: "rgba(37, 37, 37, 0.7)",
    border: "rgba(62, 62, 62, 0.3)"
  };

  const glassmorphismStyle = {
    background: theme.cardBg,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    borderRadius: "10px",
    border: `1px solid ${theme.border}`
  };

  return (
    <div
      style={{
        fontFamily: "'Roboto Mono', monospace",
        padding: "2rem",
        backgroundColor: theme.bg,
        color: theme.text,
        minHeight: "100vh",
        backgroundImage:
          "radial-gradient(circle at 10% 20%, rgb(21, 21, 21) 0%, rgb(30, 30, 30) 90.2%)"
      }}
    >
      <div
        style={{
          ...glassmorphismStyle,
          maxWidth: "800px",
          margin: "0 auto",
          padding: "2rem",
          boxShadow: "0 8px 32px 0 rgba(157, 78, 221, 0.1)",
          marginTop: "150px"
        }}
      >
        <h1
          style={{
            fontSize: "2rem",
            fontWeight: "500",
            marginBottom: "2rem",
            color: theme.text
          }}
        >
          Admin Panel
        </h1>

        <div style={{ display: "flex", gap: "1rem", marginBottom: "1.5rem" }}>
          <input
            type="text"
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            placeholder="Enter Provider ID"
            style={{
              flexGrow: 1,
              padding: "0.75rem",
              borderRadius: "4px",
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.inputBg,
              color: callDetails ? "lightgreen" : "theme.text",
              fontSize: "1rem",
              outline: "none", // This removes the default focus outline
              transition: "border-color 0.3s ease" // Smooth transition for any focus effects
            }}
            onFocus={(e) =>
              (e.target.style.borderColor = "rgba(255, 255, 255, 0.5)")
            } // Optional: lighter border on focus
            onBlur={(e) => (e.target.style.borderColor = theme.border)} // Revert border on blur
          />
          <button
            onClick={handleStartCall}
            disabled={isCallActive}
            style={{
              padding: "0.75rem 1.5rem",
              backgroundColor: isCallActive
                ? "rgba(100, 100, 100, 0.3)"
                : theme.primary,
              color: "#ffffff",
              border: "none",
              borderRadius: "4px",
              cursor: isCallActive ? "not-allowed" : "pointer",
              fontSize: "1rem",
              fontWeight: "bold",
              display: "flex",
              flexDirection: "row",
              gap: "5px"
            }}
          >
            <Phone size={13} strokeWidth={2.4} style={{ marginTop: "2.6px" }} />
            <span>Start Call</span>
          </button>
          <button
            onClick={handleEndCall}
            style={{
              padding: "0.75rem 1.5rem",
              backgroundColor: isCallActive
                ? theme.danger
                : "rgba(100, 100, 100, 0.3)",
              color: "#ffffff",
              border: "none",
              borderRadius: "4px",
              fontSize: "1rem",
              fontWeight: "bold"
            }}
          >
            End Call
          </button>
        </div>

        {callDetails && (
          <div style={{ marginBottom: "1.5rem" }}>
            <h2
              style={{
                fontSize: "1.25rem",
                fontWeight: "normal",
                marginBottom: "0.75rem"
              }}
            >
              Call Details:
            </h2>
            <pre
              style={{
                backgroundColor: theme.inputBg,
                padding: "1rem",
                borderRadius: "4px",
                overflowX: "auto",
                fontSize: "0.9rem"
              }}
            >
              {JSON.stringify(
                {
                  call_id: callDetails.call_id,
                  agent_id: callDetails.agent_id
                },
                null,
                2
              )}
            </pre>
          </div>
        )}

        <div>
          <h2
            style={{
              fontSize: "1.25rem",
              fontWeight: "normal",
              marginBottom: "0.75rem"
            }}
          >
            Transcript:
          </h2>
          <div
            style={{
              backgroundColor: theme.inputBg,
              padding: "1rem",
              borderRadius: "4px",
              minHeight: "150px",
              maxHeight: "300px",
              overflowY: "auto",
              fontSize: "0.9rem"
            }}
          >
            {transcript || "Transcript will appear here during the call."}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
